import { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ProviderChip } from '@/components/ProviderChip';
import { LIVE_PROVIDERS, PROVIDERS, COMING_SOON } from '@/constants/providers';
import { Loader2, Save, Globe, Building2, CalendarClock } from 'lucide-react';

// utils ----------------------------------------------------------------
const csvToArr = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);
const arrToCsv = (a) => (a || []).join(', ');

export default function ProjectSettingsTab({ project, reload }) {
  const [name, setName] = useState(project.name || '');
  const [domain, setDomain] = useState(project.domain || '');
  const [industry, setIndustry] = useState(project.industry || '');
  const [description, setDescription] = useState(project.description || '');
  const [competitors, setCompetitors] = useState((project.competitors || []).join(', '));
  const [keywords, setKeywords] = useState((project.keywords || []).join(', '));
  const [enabled, setEnabled] = useState(new Set(project.enabled_providers || LIVE_PROVIDERS));
  const [brandDomains, setBrandDomains] = useState(arrToCsv(project.brand_domains));
  // Map of competitor name -> comma-separated string of domains
  const [competitorDomainsCsv, setCompetitorDomainsCsv] = useState(() => {
    const m = project.competitor_domains || {};
    const out = {};
    Object.keys(m).forEach((k) => { out[k] = (m[k] || []).join(', '); });
    return out;
  });
  const [saving, setSaving] = useState(false);
  const [schedFreq, setSchedFreq] = useState(project.scheduled_frequency || '');
  const [schedHour, setSchedHour] = useState(project.scheduled_hour ?? 9);
  const [schedTz, setSchedTz] = useState(project.scheduled_timezone || 'UTC');
  const [schedAutoScan, setSchedAutoScan] = useState(!!project.scheduled_auto_scan);

  // Sync when project prop changes
  useEffect(() => {
    setName(project.name || '');
    setDomain(project.domain || '');
    setIndustry(project.industry || '');
    setDescription(project.description || '');
    setCompetitors((project.competitors || []).join(', '));
    setKeywords((project.keywords || []).join(', '));
    setEnabled(new Set(project.enabled_providers || LIVE_PROVIDERS));
    setBrandDomains(arrToCsv(project.brand_domains));
    const m = project.competitor_domains || {};
    const out = {};
    Object.keys(m).forEach((k) => { out[k] = (m[k] || []).join(', '); });
    setCompetitorDomainsCsv(out);
    setSchedFreq(project.scheduled_frequency || '');
    setSchedHour(project.scheduled_hour ?? 9);
    setSchedTz(project.scheduled_timezone || 'UTC');
    setSchedAutoScan(!!project.scheduled_auto_scan);
  }, [project]);

  const competitorList = useMemo(() => csvToArr(competitors), [competitors]);

  const toggle = (key) => {
    const next = new Set(enabled);
    if (next.has(key)) next.delete(key); else next.add(key);
    setEnabled(next);
  };

  const setCompetitorDomains = (compName, value) => {
    setCompetitorDomainsCsv((prev) => ({ ...prev, [compName]: value }));
  };

  const save = async () => {
    setSaving(true);
    try {
      const finalCompetitors = csvToArr(competitors);
      // Build competitor_domains map only for current competitors that have domains
      const cdMap = {};
      finalCompetitors.forEach((c) => {
        const arr = csvToArr(competitorDomainsCsv[c] || '');
        if (arr.length) cdMap[c] = arr;
      });

      await api.patch(`/projects/${project.id}`, {
        name: name.trim(),
        domain: domain.trim(),
        industry: industry.trim(),
        description: description.trim(),
        competitors: finalCompetitors,
        keywords: csvToArr(keywords),
        enabled_providers: Array.from(enabled),
        brand_domains: csvToArr(brandDomains),
        competitor_domains: cdMap,
        scheduled_frequency: schedFreq || null,
        scheduled_hour: schedFreq ? Number(schedHour) : null,
        scheduled_timezone: schedFreq ? schedTz : null,
        scheduled_auto_scan: schedFreq ? !!schedAutoScan : false,
      });
      toast.success('Project updated');
      reload && reload();
    } catch (_e) {
      toast.error('Failed to update');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4 max-w-3xl">
      <Card className="border p-6 space-y-4">
        <div>
          <div className="font-display text-base font-semibold">Brand details</div>
          <p className="text-sm text-muted-foreground">Keep this accurate — it powers prompt generation and analysis.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="pname">Brand name</Label>
            <Input id="pname" value={name} onChange={(e) => setName(e.target.value)} data-testid="project-settings-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pdomain">Primary domain</Label>
            <Input id="pdomain" value={domain} onChange={(e) => setDomain(e.target.value)} data-testid="project-settings-domain-input" placeholder="notion.so" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="pindustry">Industry</Label>
            <Input id="pindustry" value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="project-settings-industry-input" />
          </div>
          <div className="space-y-1.5 md:col-span-2">
            <Label htmlFor="pdesc">Description</Label>
            <Textarea id="pdesc" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="project-settings-description-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pcomp">Competitors</Label>
            <Input id="pcomp" value={competitors} onChange={(e) => setCompetitors(e.target.value)} placeholder="comma separated" data-testid="project-settings-competitors-input" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pkw">Keywords</Label>
            <Input id="pkw" value={keywords} onChange={(e) => setKeywords(e.target.value)} placeholder="comma separated" data-testid="project-settings-keywords-input" />
          </div>
        </div>
      </Card>

      {/* Domain attribution ------------------------------------------------ */}
      <Card className="border p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-[hsl(var(--accent))]" />
            <div className="font-display text-base font-semibold">Brand domains</div>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Domains you own. Citations pointing here will be attributed to your brand
            (e.g. <span className="font-mono text-xs">notion.so, help.notion.so</span>).
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pbdoms">Owned domains</Label>
          <Input
            id="pbdoms"
            value={brandDomains}
            onChange={(e) => setBrandDomains(e.target.value)}
            placeholder="notion.so, blog.notion.so, help.notion.so"
            data-testid="project-settings-brand-domains-input"
          />
        </div>
      </Card>

      <Card className="border p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <Globe className="h-4 w-4 text-[hsl(var(--accent))]" />
            <div className="font-display text-base font-semibold">Competitor domains</div>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Assign domains for each competitor to accurately attribute citations in AI answers.
          </p>
        </div>
        {competitorList.length === 0 ? (
          <div className="text-sm text-muted-foreground rounded-md border border-dashed p-4">
            Add competitors above to configure their domains here.
          </div>
        ) : (
          <div className="space-y-3">
            {competitorList.map((c) => (
              <div key={c} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-center" data-testid={`competitor-domain-row-${c}`}>
                <div className="text-sm font-medium truncate">{c}</div>
                <div className="md:col-span-2">
                  <Input
                    value={competitorDomainsCsv[c] || ''}
                    onChange={(e) => setCompetitorDomains(c, e.target.value)}
                    placeholder="e.g. evernote.com, help.evernote.com"
                    data-testid={`competitor-domain-input-${c}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Card className="border p-6 space-y-4">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-[hsl(var(--accent))]" />
            <div className="font-display text-base font-semibold">Scheduled audit reports</div>
            <span className="text-[10px] uppercase tracking-wide rounded-full border border-amber-500/40 text-amber-700 bg-amber-500/10 px-2 py-0.5">Growth+</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Auto-generate a fresh audit report on a schedule. Uses the latest completed scan — run a scan on your own cadence for freshest data.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-1.5">
            <Label htmlFor="sched-freq">Frequency</Label>
            <select
              id="sched-freq"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={schedFreq}
              onChange={(e) => setSchedFreq(e.target.value)}
              data-testid="project-settings-schedule-frequency"
            >
              <option value="">Off</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sched-hour">Hour of day (0–23)</Label>
            <Input
              id="sched-hour"
              type="number"
              min={0}
              max={23}
              value={schedHour}
              onChange={(e) => setSchedHour(e.target.value)}
              disabled={!schedFreq}
              data-testid="project-settings-schedule-hour"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="sched-tz">Timezone (IANA)</Label>
            <Input
              id="sched-tz"
              value={schedTz}
              onChange={(e) => setSchedTz(e.target.value)}
              placeholder="e.g. America/New_York"
              disabled={!schedFreq}
              data-testid="project-settings-schedule-timezone"
            />
          </div>
        </div>
        {schedFreq && (
          <>
            <div className="rounded-lg border p-3 flex items-start justify-between gap-4" data-testid="project-settings-auto-scan-row">
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  Run a fresh scan before each scheduled report
                  <span className="text-[10px] uppercase tracking-wide rounded-full border border-amber-500/40 text-amber-700 bg-amber-500/10 px-2 py-0.5">Growth+</span>
                </div>
                <div className="text-xs text-muted-foreground mt-1 leading-snug">
                  When on, we'll run a fresh multi-provider scan first, then generate the report — so the numbers are always live. Uses one scan from your monthly quota; falls back to your latest existing scan if the quota is exhausted.
                </div>
              </div>
              <Switch
                checked={schedAutoScan}
                onCheckedChange={(v) => setSchedAutoScan(!!v)}
                data-testid="project-settings-auto-scan-toggle"
              />
            </div>
            <div className="text-[11px] text-muted-foreground">
              Reports will be generated {schedFreq} at approximately {String(schedHour).padStart(2, '0')}:00 {schedTz}.
            </div>
          </>
        )}
      </Card>

      <Card className="border p-6 space-y-4">
        <div>
          <div className="font-display text-base font-semibold">Providers</div>
          <p className="text-sm text-muted-foreground">Choose which AI answer engines to include in scans.</p>
        </div>
        <div className="space-y-2">
          {LIVE_PROVIDERS.map((key) => (
            <div key={key} className="flex items-center justify-between rounded-lg border p-3" data-testid={`project-provider-toggle-${key}`}>
              <div className="flex items-center gap-3">
                <ProviderChip providerKey={key} active={enabled.has(key)} />
                <div className="text-xs text-muted-foreground">{PROVIDERS[key].label}</div>
              </div>
              <Switch checked={enabled.has(key)} onCheckedChange={() => toggle(key)} />
            </div>
          ))}
          {COMING_SOON.map((key) => (
            <div key={key} className="flex items-center justify-between rounded-lg border p-3 opacity-60">
              <div className="flex items-center gap-3">
                <ProviderChip providerKey={key} comingSoon />
                <div className="text-xs text-muted-foreground">Coming soon</div>
              </div>
              <Switch checked={false} disabled />
            </div>
          ))}
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="project-settings-save-button">
          {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />} Save changes
        </Button>
      </div>
    </div>
  );
}
